import { useEffect, useRef } from 'react';
import { isMediaModalOpen } from '../lib/mediaModal';

const SKIP_WAITING = 'SKIP_WAITING';
const RELOAD_GUARD_KEY = 'sw-reload-guard';
// Se l'app resta sempre in foreground (raro, ma possibile) applichiamo comunque
// l'aggiornamento dopo questo tempo, per non restare bloccati a vita sulla vecchia versione.
const MAX_WAIT_MS = 30 * 60 * 1000;

function reloadOnce() {
  if (sessionStorage.getItem(RELOAD_GUARD_KEY)) return false;
  sessionStorage.setItem(RELOAD_GUARD_KEY, '1');
  window.location.reload();
  return true;
}

/**
 * Registra il service worker e applica un nuovo deploy in modo silenzioso,
 * senza mai interrompere l'utente con un popup "Aggiorna ora": la nuova
 * versione viene attivata (e la pagina ricaricata) solo quando l'app va in
 * background (tab/app non visibile) — quindi in un momento in cui l'utente
 * non se ne accorge — oppure, come rete di sicurezza, dopo un tempo massimo
 * di attesa se l'app restasse sempre in primo piano.
 */
export function useServiceWorkerUpdate() {
  const appliedRef = useRef(false);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;

    let reg;
    let onUpdateFound;
    let maxWaitTimer = null;

    const applyUpdate = () => {
      if (!reg?.waiting || appliedRef.current) return;
      if (isMediaModalOpen()) return; // non interrompere un video/documento aperto
      appliedRef.current = true;
      if (maxWaitTimer) {
        clearTimeout(maxWaitTimer);
        maxWaitTimer = null;
      }
      let reloaded = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (reloaded) return;
        reloaded = true;
        reloadOnce();
      });
      reg.waiting.postMessage({ type: SKIP_WAITING });
    };

    const scheduleApply = () => {
      if (appliedRef.current) return;
      if (document.visibilityState === 'hidden') {
        applyUpdate();
        return;
      }
      if (!maxWaitTimer) {
        maxWaitTimer = setTimeout(applyUpdate, MAX_WAIT_MS);
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') applyUpdate();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    const run = async () => {
      reg = await navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' });

      try {
        await reg.update();
      } catch {
        /* offline */
      }
      if (reg.waiting && navigator.serviceWorker.controller) scheduleApply();

      onUpdateFound = () => {
        const nw = reg.installing;
        if (!nw) return;
        nw.addEventListener('statechange', () => {
          if (nw.state === 'installed' && navigator.serviceWorker.controller) {
            scheduleApply();
          }
        });
      };
      reg.addEventListener('updatefound', onUpdateFound);
    };

    run().catch(() => {});

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (reg && onUpdateFound) {
        reg.removeEventListener('updatefound', onUpdateFound);
      }
      if (maxWaitTimer) clearTimeout(maxWaitTimer);
    };
  }, []);

  useEffect(() => {
    sessionStorage.removeItem(RELOAD_GUARD_KEY);
  }, []);
}
